import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import {
  Users, Search, MoreVertical, UserCheck, UserX, Key, Trash2,
  Building2, Mail, Phone, Calendar, Shield, Loader2, RefreshCw, Eye, Edit, Save
} from 'lucide-react';
import { toast } from 'sonner';

export default function UtilizadoresPortalPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('todos');
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [resetPasswordModal, setResetPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Novo utilizador
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [instituicoes, setInstituicoes] = useState([]);
  const [newUser, setNewUser] = useState({ nome: '', email: '', instituicao_id: '', role: 'INST_ADMIN' });
  const [createdPassword, setCreatedPassword] = useState('');
  // Edição de utilizador
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({ nome: '', email: '', telefone: '', role: '' });

  // Handle URL parameter for opening specific user
  useEffect(() => {
    const userId = searchParams.get('user');
    if (userId && users.length > 0) {
      const userToOpen = users.find(u => u.id === userId);
      if (userToOpen) {
        setSelectedUser(userToOpen);
        setModalOpen(true);
        // Clear the URL parameter after opening
        setSearchParams({});
      }
    }
  }, [users, searchParams, setSearchParams]);

  useEffect(() => {
    loadUsers();
    loadInstituicoes();
  }, [filterStatus]);

  const loadInstituicoes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/instituicoes?limit=500`,
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
      if (response.ok) {
        const data = await response.json();
        setInstituicoes(Array.isArray(data) ? data : data.instituicoes || []);
      }
    } catch (error) {
      console.error('Error loading instituicoes:', error);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterStatus !== 'todos') params.append('status', filterStatus);
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/portal-users?${params}`,
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Erro ao carregar utilizadores');
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (user) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/portal-users/${user.id}`,
        { headers: { 'Authorization': `Bearer ${token}` }}
      );
      
      if (response.ok) {
        const data = await response.json();
        setSelectedUser(data);
        setModalOpen(true);
      }
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const handleToggleActive = async (userId) => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/portal-users/${userId}/toggle-active`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.ativo ? 'Utilizador ativado' : 'Utilizador desativado');
        loadUsers();
        if (selectedUser?.id === userId) {
          setSelectedUser(prev => ({ ...prev, ativo: data.ativo }));
        }
      }
    } catch (error) {
      toast.error('Erro ao atualizar utilizador');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = async (userId) => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/portal-users/${userId}/reset-password`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setNewPassword(data.temp_password);
        setResetPasswordModal(true);
        toast.success('Password resetada com sucesso');
      }
    } catch (error) {
      toast.error('Erro ao resetar password');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Tem certeza que deseja remover este utilizador?')) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/portal-users/${userId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        toast.success('Utilizador removido');
        loadUsers();
        setModalOpen(false);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erro ao remover');
      }
    } catch (error) {
      toast.error('Erro ao remover utilizador');
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = () => {
    setEditData({
      nome: selectedUser?.nome || '',
      email: selectedUser?.email || '',
      telefone: selectedUser?.telefone || '',
      role: selectedUser?.role || 'INST_USER'
    });
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditData({ nome: '', email: '', telefone: '', role: '' });
  };

  const handleSaveEdit = async () => {
    if (!editData.nome.trim() || !editData.email.trim()) {
      toast.error('Nome e email são obrigatórios');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/portal-users/${selectedUser.id}`,
        {
          method: 'PUT',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(editData)
        }
      );

      if (response.ok) {
        toast.success('Utilizador atualizado com sucesso');
        setEditMode(false);
        setModalOpen(false);  // Fechar o modal após guardar
        loadUsers();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erro ao atualizar');
      }
    } catch (error) {
      toast.error('Erro ao atualizar utilizador');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.nome || !newUser.email || !newUser.instituicao_id) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/portal-users`,
        {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(newUser)
        }
      );
      
      const data = await response.json();
      
      if (response.ok) {
        setCreatedPassword(data.temp_password);
        toast.success('Utilizador criado com sucesso!');
        loadUsers();
      } else {
        toast.error(data.detail || 'Erro ao criar utilizador');
      }
    } catch (error) {
      toast.error('Erro ao criar utilizador');
    } finally {
      setSubmitting(false);
    }
  };

  const closeCreateModal = () => {
    setCreateModalOpen(false);
    setNewUser({ nome: '', email: '', instituicao_id: '', role: 'INST_ADMIN' });
    setCreatedPassword('');
  };

  const filteredUsers = users.filter(u =>
    u.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.instituicao_nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.codigo_ea?.includes(searchTerm)
  );

  const stats = {
    total: users.length,
    ativos: users.filter(u => u.ativo).length,
    inativos: users.filter(u => !u.ativo).length,
    admins: users.filter(u => u.role === 'admin').length
  };

  return (
    <div className="space-y-6" data-testid="utilizadores-portal-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Utilizadores do Portal</h1>
          <p className="text-slate-500 dark:text-slate-400">Gestão de acessos ao Portal das Instituições</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setCreateModalOpen(true)} data-testid="create-user-btn">
            <Users className="w-4 h-4 mr-2" />
            Criar Utilizador
          </Button>
          <Button onClick={loadUsers} variant="outline" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:shadow-md" onClick={() => setFilterStatus('todos')}>
          <CardContent className="pt-4 text-center">
            <Users className="w-8 h-8 mx-auto mb-2 text-slate-400" />
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.total}</p>
            <p className="text-xs text-slate-500">Total</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer hover:shadow-md border-l-4 border-l-green-500 ${filterStatus === 'ativos' ? 'ring-2 ring-green-500' : ''}`} onClick={() => setFilterStatus('ativos')} data-testid="filter-card-ativos">
          <CardContent className="pt-4 text-center">
            <UserCheck className="w-8 h-8 mx-auto mb-2 text-green-500" />
            <p className="text-2xl font-bold text-green-600">{stats.ativos}</p>
            <p className="text-xs text-slate-500">Ativos</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer hover:shadow-md border-l-4 border-l-red-500 ${filterStatus === 'inativos' ? 'ring-2 ring-red-500' : ''}`} onClick={() => setFilterStatus('inativos')} data-testid="filter-card-inativos">
          <CardContent className="pt-4 text-center">
            <UserX className="w-8 h-8 mx-auto mb-2 text-red-500" />
            <p className="text-2xl font-bold text-red-600">{stats.inativos}</p>
            <p className="text-xs text-slate-500">Inativos</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer hover:shadow-md ${filterStatus === 'admins' ? 'ring-2 ring-purple-500' : ''}`} onClick={() => setFilterStatus('admins')} data-testid="filter-card-admins">
          <CardContent className="pt-4 text-center">
            <Shield className="w-8 h-8 mx-auto mb-2 text-purple-500" />
            <p className="text-2xl font-bold text-purple-600">{stats.admins}</p>
            <p className="text-xs text-slate-500">Administradores</p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Pesquisar por nome, email, instituição ou código EA..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Utilizador</TableHead>
                  <TableHead>Instituição</TableHead>
                  <TableHead>Código EA</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Último Login</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      Nenhum utilizador encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => (
                    <TableRow key={user.id} className="hover:bg-slate-50 dark:hover:bg-slate-800" data-testid={`user-row-${user.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium dark:text-white">{user.nome}</p>
                          <p className="text-xs text-slate-500">{user.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-400" />
                          <span className="dark:text-white">{user.instituicao_nome}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{user.codigo_ea || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={user.role === 'admin' ? 'border-purple-500 text-purple-500' : ''}>
                          {user.role === 'admin' ? 'Admin' : 'Utilizador'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={user.ativo ? 'bg-green-500' : 'bg-red-500'}>
                          {user.ativo ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-500">
                        {user.last_login ? new Date(user.last_login).toLocaleDateString('pt-PT') : 'Nunca'}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openDetail(user)}>
                              <Eye className="w-4 h-4 mr-2" />
                              Ver Detalhes
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { openDetail(user); setTimeout(() => startEdit(), 100); }}>
                              <Edit className="w-4 h-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleActive(user.id)}>
                              {user.ativo ? <UserX className="w-4 h-4 mr-2" /> : <UserCheck className="w-4 h-4 mr-2" />}
                              {user.ativo ? 'Desativar' : 'Ativar'}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleResetPassword(user.id)}>
                              <Key className="w-4 h-4 mr-2" />
                              Resetar Password
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(user.id)} className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Remover
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={modalOpen} onOpenChange={(open) => { if (!open) { setEditMode(false); } setModalOpen(open); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Users className="w-5 h-5 text-[#0066CC]" />
              {editMode ? 'Editar Utilizador' : selectedUser?.nome}
            </DialogTitle>
            <DialogDescription>
              {editMode ? 'Edite os dados do utilizador' : 'Detalhes do utilizador do portal'}
            </DialogDescription>
          </DialogHeader>

          {selectedUser && (
            <div className="space-y-4">
              {editMode ? (
                /* Edit Mode */
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nome *</Label>
                    <Input
                      value={editData.nome}
                      onChange={(e) => setEditData({ ...editData, nome: e.target.value })}
                      placeholder="Nome completo"
                      data-testid="edit-nome"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={editData.email}
                      onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                      placeholder="email@instituicao.pt"
                      data-testid="edit-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Telefone</Label>
                    <Input
                      value={editData.telefone}
                      onChange={(e) => setEditData({ ...editData, telefone: e.target.value })}
                      placeholder="912 345 678"
                      data-testid="edit-telefone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Perfil</Label>
                    <select
                      className="w-full px-3 py-2 border border-slate-200 rounded-md dark:bg-slate-800 dark:border-slate-700"
                      value={editData.role}
                      onChange={(e) => setEditData({ ...editData, role: e.target.value })}
                      data-testid="edit-role"
                    >
                      <option value="INST_ADMIN">Administrador</option>
                      <option value="INST_USER">Utilizador</option>
                    </select>
                  </div>
                  <div className="flex gap-2 pt-4 border-t">
                    <Button 
                      variant="outline" 
                      onClick={cancelEdit}
                      disabled={submitting}
                      className="flex-1"
                    >
                      Cancelar
                    </Button>
                    <Button 
                      onClick={handleSaveEdit}
                      disabled={submitting}
                      className="flex-1"
                      data-testid="save-edit-btn"
                    >
                      {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Guardar
                    </Button>
                  </div>
                </div>
              ) : (
                /* View Mode */
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-slate-500">Email</Label>
                      <p className="flex items-center gap-2 dark:text-white">
                        <Mail className="w-4 h-4 text-slate-400" />
                        {selectedUser.email}
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Telefone</Label>
                      <p className="flex items-center gap-2 dark:text-white">
                        <Phone className="w-4 h-4 text-slate-400" />
                        {selectedUser.telefone || '-'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Role</Label>
                      <Badge variant="outline" className={selectedUser.role === 'admin' || selectedUser.role === 'INST_ADMIN' ? 'border-purple-500 text-purple-500' : ''}>
                        {selectedUser.role === 'admin' || selectedUser.role === 'INST_ADMIN' ? 'Administrador' : 'Utilizador'}
                      </Badge>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Status</Label>
                      <Badge className={selectedUser.ativo ? 'bg-green-500' : 'bg-red-500'}>
                        {selectedUser.ativo ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Data de Registo</Label>
                      <p className="flex items-center gap-2 dark:text-white">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        {selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleDateString('pt-PT') : '-'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Último Login</Label>
                      <p className="dark:text-white">
                        {selectedUser.last_login ? new Date(selectedUser.last_login).toLocaleString('pt-PT') : 'Nunca'}
                      </p>
                    </div>
                  </div>

                  {/* Institution Info */}
                  {(selectedUser.instituicao || selectedUser.instituicao_nome) && (
                    <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <Label className="text-xs text-slate-500 mb-2 block">Instituição</Label>
                      <div className="flex items-center gap-3">
                        <Building2 className="w-8 h-8 text-[#0066CC]" />
                        <div>
                          <p className="font-medium dark:text-white">{selectedUser.instituicao?.nome || selectedUser.instituicao_nome}</p>
                          <p className="text-sm text-slate-500">
                            Código: {selectedUser.instituicao?.codigo_ea || selectedUser.codigo_ea || 'Pendente'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 pt-4 border-t">
                    <Button 
                      variant="outline" 
                      onClick={startEdit}
                      disabled={submitting}
                      className="flex-1"
                      data-testid="edit-user-btn"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Editar
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => handleToggleActive(selectedUser.id)}
                      disabled={submitting}
                      className="flex-1"
                    >
                      {selectedUser.ativo ? <UserX className="w-4 h-4 mr-2" /> : <UserCheck className="w-4 h-4 mr-2" />}
                      {selectedUser.ativo ? 'Desativar' : 'Ativar'}
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => handleResetPassword(selectedUser.id)}
                      disabled={submitting}
                      className="flex-1"
                    >
                      <Key className="w-4 h-4 mr-2" />
                      Resetar Password
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditMode(false); setModalOpen(false); }}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Result Modal */}
      <Dialog open={resetPasswordModal} onOpenChange={setResetPasswordModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <Key className="w-5 h-5" />
              Password Resetada
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-slate-500">Nova password temporária gerada:</p>
            <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-lg text-center">
              <code className="text-lg font-mono font-bold text-[#0066CC]">{newPassword}</code>
            </div>
            <p className="text-sm text-amber-600">
              Guarde esta password! O utilizador deverá alterá-la no próximo login.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => { setResetPasswordModal(false); setNewPassword(''); }}>
              Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Modal */}
      <Dialog open={createModalOpen} onOpenChange={(open) => !open && closeCreateModal()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#0066CC]" />
              Criar Utilizador do Portal
            </DialogTitle>
            <DialogDescription>
              Crie um acesso ao Portal das Instituições para uma instituição
            </DialogDescription>
          </DialogHeader>
          
          {createdPassword ? (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                <UserCheck className="w-12 h-12 mx-auto text-green-500 mb-2" />
                <p className="font-medium text-green-700">Utilizador criado com sucesso!</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-2">Password temporária:</p>
                <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg text-center">
                  <code className="text-lg font-mono font-bold text-[#0066CC]">{createdPassword}</code>
                </div>
              </div>
              <p className="text-sm text-amber-600">
                ⚠️ Guarde esta password! Ela só será mostrada uma vez. O utilizador deverá alterá-la no primeiro login.
              </p>
              <DialogFooter>
                <Button onClick={closeCreateModal}>Fechar</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Nome *</Label>
                <Input
                  placeholder="Nome completo"
                  value={newUser.nome}
                  onChange={(e) => setNewUser({ ...newUser, nome: e.target.value })}
                  data-testid="new-user-nome"
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  placeholder="email@instituicao.pt"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  data-testid="new-user-email"
                />
              </div>
              <div className="space-y-2">
                <Label>Instituição *</Label>
                <select
                  className="w-full px-3 py-2 border border-slate-200 rounded-md dark:bg-slate-800 dark:border-slate-700"
                  value={newUser.instituicao_id}
                  onChange={(e) => setNewUser({ ...newUser, instituicao_id: e.target.value })}
                  data-testid="new-user-instituicao"
                >
                  <option value="">Selecione uma instituição...</option>
                  {instituicoes.map(inst => (
                    <option key={inst.id} value={inst.id}>
                      {inst.codigo_ea || 'S/C'} - {inst.nome}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Perfil</Label>
                <select
                  className="w-full px-3 py-2 border border-slate-200 rounded-md dark:bg-slate-800 dark:border-slate-700"
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                >
                  <option value="INST_ADMIN">Administrador</option>
                  <option value="INST_USER">Utilizador</option>
                </select>
              </div>
              <DialogFooter className="mt-6">
                <Button variant="outline" onClick={closeCreateModal} disabled={submitting}>
                  Cancelar
                </Button>
                <Button onClick={handleCreateUser} disabled={submitting}>
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Criar Utilizador
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
